import { motion } from 'framer-motion';
import { useRoomStore } from '../../store/room.store.js';
import { useAuthStore } from '../../store/auth.store.js';

interface CountdownPanelProps {
  roomCode: string;
  onLeave: () => void;
}

export function CountdownPanel({ roomCode, onLeave }: CountdownPanelProps) {
  const { players, startGame, roomDetails, activeRoom } = useRoomStore();
  const { user } = useAuthStore();
  const playerCount = players.length;
  const maxPlayers = roomDetails?.maxPlayers || 30;

  // Check if current user is the host using sessionId
  const currentPlayer = players.find(p => p.id === activeRoom?.sessionId);
  const isHost = currentPlayer?.isHost ?? false;
  const canStart = isHost && playerCount >= 2;

  const handleInviteClick = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      alert(`Room code "${roomCode}" copied to clipboard! Share it with your friends.`);
    } catch {
      alert(`Room code: ${roomCode}`);
    }
  };

  return (
    <footer className="w-full flex items-center justify-between px-6 py-4 border-t border-white/5 bg-slate-950/20 z-30">
      {/* Left: Status */}
      <div className="flex items-center space-x-3 text-left">
        <div className="w-8 h-8 rounded-full bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-sm">
          ⏳
        </div>
        <div>
          <span className="block text-[9px] uppercase tracking-wider text-surface-400 font-bold">
            Room Status
          </span>
          <span className="text-xs font-black text-amber-300 font-mono tracking-wide">
            {playerCount < 2
              ? `Waiting for players... (${playerCount}/${maxPlayers} joined, min 2)`
              : isHost
                ? `${playerCount}/${maxPlayers} players ready — Start the game!`
                : `${playerCount}/${maxPlayers} players ready — Waiting for host...`}
          </span>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center space-x-3">
        {/* Start Game button (host only) */}
        {canStart && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startGame}
            className="flex items-center space-x-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white text-xs font-black uppercase tracking-widest cursor-pointer shadow-[0_0_20px_rgba(16,185,129,0.4)] animate-pulse hover:animate-none"
          >
            <span>🚀</span>
            <span>Start Game</span>
          </motion.button>
        )}

        {/* Invite button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleInviteClick}
          className="flex items-center space-x-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white text-xs font-black uppercase tracking-widest cursor-pointer shadow-glow"
        >
          <span>🔗</span>
          <span>Invite</span>
        </motion.button>

        {/* Leave button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onLeave}
          className="flex items-center space-x-1.5 px-4 py-2 rounded-full bg-slate-900/40 hover:bg-slate-900/70 border border-white/5 text-surface-200 hover:text-white text-xs font-bold uppercase tracking-wider cursor-pointer"
        >
          <span>🚪</span>
          <span>Leave</span>
        </motion.button>
      </div>
    </footer>
  );
}

